import { FormEvent, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Link, Stack, TextField, Typography } from '@mui/material';
import { ApiError, resendVerificationRequest } from '@/auth/api';
import { safeNextPath, withNext } from '@/auth/nextPath';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

export function CustomerCheckEmailPage() {
  const location = useLocation();
  const stateEmail =
    typeof location.state === 'object' &&
    location.state &&
    'email' in location.state &&
    typeof location.state.email === 'string'
      ? location.state.email
      : '';
  // Where the registration was heading, like the quotation form (ADR 0008).
  const next =
    typeof location.state === 'object' && location.state && 'next' in location.state
      ? safeNextPath(typeof location.state.next === 'string' ? location.state.next : null)
      : null;
  const [email, setEmail] = useState(stateEmail);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleResend = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(null);
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await resendVerificationRequest(email);
      setMessage(result.message);
    } catch (error) {
      setErrorMessage(
        error instanceof ApiError && error.status === 429
          ? 'Ya solicitaste un enlace hace poco. Esperá unos minutos antes de pedir otro.'
          : 'No pudimos solicitar otro enlace. Volvé a intentar.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NarrowPage maxWidth={600}>
      <PageHeader
        eyebrow="VERIFICACIÓN DE EMAIL"
        title="Revisá tu email"
        description="Te enviamos un enlace que vence en 24 horas. Abrilo para verificar tu cuenta."
      />
      {next ? (
        <Alert severity="info">
          Para seguir con tu cotización no hace falta verificar el email:{' '}
          <Link component={RouterLink} to={withNext('/login', next)} underline="always">
            ingresá con tu email y contraseña
          </Link>
          . Si PhoneTec ya te había creado una cuenta en el local, el enlace que te enviamos es para
          activarla y elegir tu contraseña.
        </Alert>
      ) : null}
      <SectionCard
        title="¿No llegó?"
        description="Revisá también las carpetas de spam o correo no deseado."
      >
        <Stack component="form" spacing={2} onSubmit={handleResend}>
          {message ? <Alert severity="success">{message}</Alert> : null}
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <TextField
            label="Email de tu cuenta"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
          />
          <PrimaryButton type="submit" disabled={isSubmitting || !email.trim()}>
            {isSubmitting ? 'Solicitando...' : 'Reenviar enlace'}
          </PrimaryButton>
          <Typography variant="body2" color="text.secondary">
            También podés{' '}
            <Link component={RouterLink} to={withNext('/login', next)} underline="hover">
              ingresar a tu cuenta
            </Link>{' '}
            aunque todavía no esté verificada.
          </Typography>
        </Stack>
      </SectionCard>
    </NarrowPage>
  );
}
